import React, { useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import BookingSummaryCard from './components/BookingSummaryCard';
import NextStepsSection from './components/NextStepsSection';
import ProfessionalContactCard from './components/ProfessionalContactCard';
import ActionButtons from './components/ActionButtons';

const generateReference = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return 'LL-' + Array.from({ length: 8 }, () => chars?.[Math.floor(Math.random() * chars?.length)])?.join('');
};

const ConsultationBookingConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const bookingData = useMemo(() => {
    const referenceNumber = sessionStorage.getItem('bookingRef') || generateReference();
    if (!sessionStorage.getItem('bookingRef')) {
      sessionStorage.setItem('bookingRef', referenceNumber);
    }

    // Primary: read from navigation state (passed by BookingPanel)
    const stateDetails = location?.state?.bookingDetails;

    if (stateDetails) {
      return {
        referenceNumber,
        professionalName: stateDetails?.professionalName || 'Professional',
        professionalCategory: stateDetails?.professionalCategory || 'Consultant',
        professionalCompany: stateDetails?.professionalCompany || '',
        professionalPhoto: stateDetails?.professionalPhoto || null,
        serviceType: stateDetails?.service || 'Consultation',
        preferredDate: stateDetails?.date || '',
        preferredTime: stateDetails?.time || '',
        clientName: stateDetails?.clientName || 'Valued Client',
        professionalPhone: stateDetails?.professionalPhone || '',
        professionalEmail: stateDetails?.professionalEmail || '',
        responseTime: stateDetails?.responseTime || '< 2 hours',
      };
    }

    // Fallback: read from URL search params (for direct URL access)
    return {
      referenceNumber,
      professionalName: searchParams?.get('name') || 'Professional',
      professionalCategory: searchParams?.get('category') || 'Consultant',
      professionalCompany: searchParams?.get('company') || '',
      professionalPhoto: searchParams?.get('photo') || null,
      serviceType: searchParams?.get('service') || 'Consultation',
      preferredDate: searchParams?.get('date') || '',
      preferredTime: searchParams?.get('time') || '',
      clientName: searchParams?.get('client') || 'Valued Client',
      professionalPhone: searchParams?.get('phone') || '',
      professionalEmail: searchParams?.get('email') || '',
      responseTime: searchParams?.get('responseTime') || '< 2 hours',
    };
  }, [location?.state, searchParams]);

  const professional = {
    name: bookingData?.professionalName,
    phone: bookingData?.professionalPhone,
    email: bookingData?.professionalEmail,
    responseTime: bookingData?.responseTime,
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0A1628 0%, #0D1B2A 50%, #0A1628 100%)' }}>
      <Helmet>
        <title>Booking Confirmed | ListingLens</title>
      </Helmet>

      {/* Header Nav */}
      <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(0,212,255,0.1)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/professional-marketplace')}
            className="flex items-center gap-2 text-sm transition-colors"
            style={{ color: '#6B9EC4' }}
          >
            <Icon name="ArrowLeft" size={16} />
            Back to Marketplace
          </button>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: '#00FF88' }} />
            <span className="text-xs font-medium" style={{ color: '#00FF88' }}>Booking Submitted</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: 'rgba(0,255,136,0.1)', border: '2px solid rgba(0,255,136,0.3)', boxShadow: '0 0 30px rgba(0,255,136,0.2)' }}
          >
            <Icon name="CheckCircle" size={40} color="#00FF88" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Booking Request Submitted!</h1>
          <p className="text-base max-w-lg mx-auto" style={{ color: '#A0B4C8' }}>
            Your consultation request has been successfully sent to <span className="text-white font-semibold">{bookingData?.professionalName}</span>. They will review and respond to confirm your appointment.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <BookingSummaryCard bookingData={bookingData} />
            <NextStepsSection />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            <ProfessionalContactCard professional={professional} />

            {/* Quick Info Card */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.15)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Info" size={16} color="#FFD700" />
                <p className="text-sm font-bold text-white">Important Note</p>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: '#A0B4C8' }}>
                This is a consultation request, not a confirmed booking. Your appointment will be confirmed once the professional accepts and schedules a time.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8">
          <ActionButtons bookingData={{ ...bookingData, professionalEmail: bookingData?.professionalEmail }} />
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-xs" style={{ color: '#6B9EC4' }}>
            A confirmation email will be sent to <span className="font-medium" style={{ color: '#A0B4C8' }}>{bookingData?.clientName}</span> once the professional accepts your request.
            Reference: <span className="font-bold" style={{ color: '#00FF88' }}>{bookingData?.referenceNumber}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsultationBookingConfirmation;
